/* sb-dc-preupload.js - make and upload the print file while the customer is
   reading the proof, so approving is not a 6-second wait.
   2026-09-18 (v4 - abortable, and an approve that lands mid-upload waits for
   it instead of racing it; see APPROVE DURING UPLOAD. Also records when the
   proof opened, for sb-dc-addprogress.js's cart_speed log).

   WHY (measured 2026-09-18, visible tab, 8x4ft vinyl banner with a photo)

       Add to Cart  ->  proof modal            2.5s
       ... customer reads the proof ...        5-15s of nothing happening
       Approve      ->  in the cart           11.0s
                        render      ~3.7s
                        sign         0.59s
                        decode       0.49s
                        UPLOAD 38MB  5.80s   <- 53% of the wait
                        finalize     0.97s

   The design is frozen the moment the proof opens: the modal says it cannot be
   changed after approval and the editor locks behind it. So the file the
   customer is about to approve is built and sent while they are still reading,
   and by the time they click, the bytes are already in the bucket.
   Measured after: approve -> cart 4.95s, with the upload at 0.

   HOW
     - the proof modal appearing is the trigger;
     - window.__dcRenderSide('front') is the app's own renderer and it is
       exposed, so the same pixels come out. It returns { blob, dataUrl, dpi,
       pixelWidth, pixelHeight };
     - the blob goes up under a temporary id, pre_<random>;
     - on approve, sb-dc-upload.js compares what the bundle produced against
       the hashes kept here, and if it is the same file asks the function to
       move the pre-uploaded object onto the real design id - a server-side
       copy, no second transfer. Anything else takes the old path.

   WHY NOT dataUrl (measured 2026-09-18)
   An earlier version hashed res.dataUrl, assuming it was the same bytes as
   res.blob. It is not - the renderer encodes the two separately:

       blob      40,043,628 bytes  -> base64 would be 53,391,504
       dataUrl   base64 length      53,390,760      (744 short)

   so the comparison never matched and every add fell back to the full upload.
   The base64 is now produced from the blob itself with FileReader, which is by
   definition the encoding of the bytes that were uploaded.

   APPROVE DURING UPLOAD (fixed 2026-09-18)
   The pre-upload of a 38MB file takes 13-15s. A customer who approved before
   it finished used to get the worst of both: the pre-upload kept sending while
   the bundle's own upload started, the two split the same uplink, and the add
   took 23s - slower than with no pre-upload at all.

   So the run publishes what it is doing:
       window.__dcPreInFlight = { promise, abort() }
   sb-dc-upload.js waits on that promise when it finds one - the file is
   usually seconds from done - and calls abort() if it decides not to use it,
   which cancels the PUT and gives the uplink back immediately.

   The hash is the whole safety argument: what ends up on the order is only
   ever the bundle's own render of the approved design, or a byte-identical
   copy. A stale design hashes differently and takes the normal path.

   Unused pre-uploads are deleted with the delete token the function returns.

   Only runs when the tab is visible - a hidden tab cannot render anyway, which
   is the separate problem sb-dc-addprogress.js warns about.

   Diagnostics: window.__dcPreUpload() */
(function () {
  'use strict';

  var FN = 'https://uktrkymhgmzlufzxsavf.supabase.co/functions/v1/dc-print-upload';
  var TICK = 600;
  var MIN_GAP = 4000;        // do not rebuild more often than this

  var info = {
    state: 'idle', runs: 0, lastMs: 0, mb: 0,
    hash: null, b64len: 0, tempId: null, why: '', openedAt: 0
  };
  window.__dcPreUpload = function () { return info; };

  /* What sb-dc-upload.js reads when the bundle finally posts its own file. */
  window.__dcPreReady = null;     // { hash, b64hash, b64len, tempId, size, at, deleteToken }
  window.__dcPreInFlight = null;  // { promise, abort } while a run is going

  function post(body) {
    return fetch(FN, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.text().then(function (t) {
        var j = null;
        try { j = t ? JSON.parse(t) : null; } catch (e) {}
        if (!r.ok || !j || !j.ok) throw new Error('HTTP ' + r.status + ' ' + String(t).slice(0, 120));
        return j;
      });
    });
  }

  function hex(buf) {
    var a = new Uint8Array(buf), out = '';
    for (var i = 0; i < a.length; i++) out += a[i].toString(16).padStart(2, '0');
    return out;
  }
  function sha256Blob(blob) {
    return blob.arrayBuffer()
      .then(function (buf) { return crypto.subtle.digest('SHA-256', buf); })
      .then(hex);
  }
  function sha256Text(s) {
    return crypto.subtle.digest('SHA-256', new TextEncoder().encode(s)).then(hex);
  }

  /* The base64 of these exact bytes - the same string the bundle will hand to
     sb-dc-upload.js when the customer approves. */
  function blobToB64(blob) {
    return new Promise(function (res, rej) {
      var fr = new FileReader();
      fr.onload = function () {
        var s = String(fr.result || '');
        var c = s.indexOf(',');
        res(c > 0 ? s.slice(c + 1) : '');
      };
      fr.onerror = function () { rej(new Error('FileReader failed')); };
      fr.readAsDataURL(blob);
    });
  }

  function tempId() {
    return 'pre_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);
  }

  /* Drop a pre-upload the customer never approved. */
  function discard(rec) {
    if (!rec || !rec.tempId || !rec.deleteToken) return;
    post({ action: 'delete', designId: rec.tempId, side: 'front', deleteToken: rec.deleteToken })
      .catch(function () {});
  }

  function run() {
    if (info.state === 'working') return;
    if (typeof window.__dcRenderSide !== 'function') { info.why = 'no renderer'; return; }
    if (document.visibilityState === 'hidden') { info.why = 'tab hidden'; return; }

    info.state = 'working';
    info.why = '';
    var t0 = Date.now();
    var mine = tempId();
    var ac = (typeof AbortController === 'function') ? new AbortController() : null;

    var p = Promise.resolve(window.__dcRenderSide('front')).then(function (res) {
      var blob = res && res.blob;
      if (!blob) throw new Error('renderer returned no blob');
      info.mb = +(blob.size / 1048576).toFixed(2);

      /* Upload first: the customer is reading, and the hashes are only needed
         by the time they click. */
      return post({ action: 'sign', designId: mine, side: 'front' }).then(function (s) {
        return fetch(s.uploadUrl, {
          method: 'PUT',
          headers: { 'content-type': 'image/png', 'x-upsert': 'true' },
          body: blob,
          signal: ac ? ac.signal : undefined
        });
      }).then(function (r) {
        if (!r.ok) throw new Error('PUT HTTP ' + r.status);
        return post({ action: 'finalize', designId: mine, side: 'front' });
      }).then(function (f) {
        return Promise.all([sha256Blob(blob), blobToB64(blob).then(function (b64) {
          return sha256Text(b64).then(function (h) { return { h: h, len: b64.length }; });
        })]).then(function (out) {
          var old = window.__dcPreReady;
          window.__dcPreReady = {
            hash: out[0], b64hash: out[1].h, b64len: out[1].len,
            tempId: mine, size: blob.size, at: Date.now(),
            deleteToken: f.deleteToken
          };
          if (old && old.tempId !== mine) discard(old);
          info.hash = String(out[0]).slice(0, 12);
          info.b64len = out[1].len;
          info.tempId = mine;
          info.runs++;
          info.lastMs = Date.now() - t0;
          info.state = 'ready';
        });
      });
    }).catch(function (e) {
      info.state = 'idle';
      info.why = (e && e.name === 'AbortError') ? 'aborted' : ('failed: ' + (e && e.message));
      /* Nothing to undo: with no __dcPreReady the normal path runs. */
    });

    window.__dcPreInFlight = {
      promise: p,
      abort: function () { try { if (ac) ac.abort(); } catch (e) {} }
    };
    p.then(function () { window.__dcPreInFlight = null; },
           function () { window.__dcPreInFlight = null; });
  }

  /* ---- when to run ------------------------------------------------------ */
  function proofOpen() {
    var b = document.querySelectorAll('button');
    for (var i = 0; i < b.length; i++) {
      if (/approve and add to cart/i.test(b[i].textContent || '') &&
          b[i].getBoundingClientRect().width > 0) return true;
    }
    return false;
  }

  var wasOpen = false, lastRun = 0;
  setInterval(function () {
    var open = proofOpen();
    if (open && !wasOpen) info.openedAt = Date.now();   // read by sb-dc-addprogress.js's log
    if (open && !wasOpen && (Date.now() - lastRun) > MIN_GAP) {
      lastRun = Date.now();
      run();
    }
    if (!open && wasOpen && info.state !== 'working') {
      /* Closed without approving - if the add went through, sb-dc-upload.js
         has already consumed the pre-upload and cleared it. */
      if (window.__dcPreReady) {
        discard(window.__dcPreReady);
        window.__dcPreReady = null;
        info.state = 'idle';
      }
    }
    wasOpen = open;
  }, TICK);
})();
